import {Component, OnInit} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {AuditLog, AuditLogResponse} from "../../shared/api/audits/api.models";
import {User} from "../../shared/api/users/api.models";
import {Company} from "../../shared/api/companies/api.models";
import {Utils} from "../../shared/utils.service";
import {Api} from "../../shared/api.service";
import * as moment from 'moment';
import {NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {GroupedLanguages} from "../../shared/languages.resolver";
import {MessageCollection} from "../../shared/api/messages/api.models";
import {Attachment} from "../../shared/api/attachments/api.models";
import {AuthenticationService} from "../../shared/authentication.service";
import {Permission} from "../../shared/api/permissions/api.models";
import {Language} from "../../shared/api/languages/api.models";
import {Observable} from "rxjs/Observable";
import {Observer} from "rxjs/Observer";


class MessageStatisticRow {
    name: string;
    removedRecord: boolean = false;
    written: number        = 0;
    approved: number       = 0;
    disapproved: number    = 0;
    deleted: number        = 0;
    total: number          = 0;
}

class MessageType {
    label: string;
    types: string[];
}

class AvailableFilters {
    companyIds      : number[] = [];
    userIds         : number[] = [];
    messageTypes    : string[] = [];
    languages       : number[] = [];
    behaviours      : string[] = [];
    attachments     : string[] = [];
}

@Component({
    selector: 'statistics-messages',
    template: require('./messages.component.html'),
    styles: [require('./messages.component.scss')]
})

export class MessagesComponent implements OnInit {
    static readonly DATE_RANGE_TODAY          = 'Today';
    static readonly DATE_RANGE_YESTERDAY      = 'Yesterday';
    static readonly DATE_RANGE_THIS_WEEK      = 'This week';
    static readonly DATE_RANGE_THIS_MONTH     = 'This month';
    static readonly DATE_RANGE_THIS_YEAR      = 'This year';
    static readonly DATE_RANGE_PREVIOUS_WEEK  = 'Previous week';
    static readonly DATE_RANGE_PREVIOUS_MONTH = 'Previous month';
    static readonly DATE_RANGE_CUSTOM         = 'Custom';

    static readonly GROUP_BY_USER              = 'User';
    static readonly GROUP_BY_LOCALE            = 'Language';
    static readonly GROUP_BY_MESSAGE_TYPE      = 'Message type';
    static readonly GROUP_BY_DATE              = 'Date';

    static readonly SORT_ASC                   = 'ASC';
    static readonly SORT_DESC                  = 'DESC';

    protected readonly dateRanges: string[] = [
        MessagesComponent.DATE_RANGE_TODAY,
        MessagesComponent.DATE_RANGE_YESTERDAY,
        MessagesComponent.DATE_RANGE_THIS_WEEK,
        MessagesComponent.DATE_RANGE_THIS_MONTH,
        MessagesComponent.DATE_RANGE_THIS_YEAR,
        MessagesComponent.DATE_RANGE_PREVIOUS_WEEK,
        MessagesComponent.DATE_RANGE_PREVIOUS_MONTH,
        MessagesComponent.DATE_RANGE_CUSTOM
    ];

    protected readonly groupBys: string[] = [
        MessagesComponent.GROUP_BY_USER,
        MessagesComponent.GROUP_BY_LOCALE,
        MessagesComponent.GROUP_BY_MESSAGE_TYPE,
        MessagesComponent.GROUP_BY_DATE
    ];

    protected startMinDate: NgbDateStruct;
    protected startMaxDate: NgbDateStruct;
    protected endMinDate: NgbDateStruct;
    protected endMaxDate: NgbDateStruct;

    protected audits: AuditLog[] = [];
    protected users: User[] = [];
    protected usersHash: any = {};
    protected companies: Company[] = [];
    protected languages: GroupedLanguages[] = [];
    protected languagesAvailable: number = 0;
    protected behaviours: string[] = [];
    protected attachments: string[] = [];
    protected types: MessageType[] = [];

    protected rows: MessageStatisticRow[]   = [];
    protected rowTotal: MessageStatisticRow = new MessageStatisticRow();
    protected rowHash: any = {};
    protected isLoading: boolean  = false;

    protected sortColumn: string    = null;
    protected sortDirection: string = null;

    protected columns: any = {
        written     : true,
        approved    : true,
        disapproved : true,
        deleted     : true,
        total       : true
    };

    protected showCompanyFilter: boolean = false;
    protected showUsersFilter: boolean   = false;

    protected filters: any;

    constructor(private route: ActivatedRoute, private utils: Utils, private api: Api, private auth: AuthenticationService) {}

    ngOnInit() {
        this.utils.hash(this.usersHash, this.route.snapshot.data['users']);

        if (this.auth.canDoAny([Permission.USERS_READ_ALL, Permission.USERS_READ]) === false) {
            this.columns.written     = this.auth.canDoAny([Permission.MESSAGES_WRITE_NEW_MESSAGES]);
            this.columns.approved    = this.auth.canDoAny([Permission.MESSAGES_CHECK_MESSAGES]);
            this.columns.disapproved = this.columns.approved;
            this.columns.deleted     = this.auth.canDoAny([Permission.MESSAGES_DELETE]);
        }

        this.resetFilters();
        this.reload();
    }

    export(event: Event) {
        let lines: any[] = [
            '"' + this.filters.groupBy + '","written","approved","disapproved","total"'
        ];

        this.rows.forEach((row: MessageStatisticRow) => {
            lines.push(
                '"' +
                [
                    row.name,
                    row.written,
                    row.approved,
                    row.disapproved,
                    row.total
                ].join('","') +
                '"'
            )
        });

        lines.push(
            '"' +
            [
                this.rowTotal.name,
                this.rowTotal.written,
                this.rowTotal.approved,
                this.rowTotal.disapproved,
                this.rowTotal.total
            ].join('","') +
            '"'
        );

        event.currentTarget['href']     = window.URL.createObjectURL(new Blob([lines.join("\n")], {type : 'text/csv'}));
        event.currentTarget['download'] = 'message_statistics.csv';
    }

    private populateStatisticTable(audits: AuditLog[], reloadFilters: boolean): void {
        let languageIdHash   = {};
        let availableFilters = new AvailableFilters();

        this.rows          = [];
        this.rowHash       = {};
        this.rowTotal      = new MessageStatisticRow();
        this.rowTotal.name = 'Total';

        let groupField: (log: AuditLog) => any;
        let groupLabel: (log: AuditLog) => string;
        let isRemoved:  (log: AuditLog) => boolean;

        switch (this.filters.groupBy) {
            case MessagesComponent.GROUP_BY_USER:
                groupField = (log: AuditLog) => log.userId;
                groupLabel = (log: AuditLog) => this.usersHash[log.userId] ? this.usersHash[log.userId].username : log.metadata.username;
                isRemoved  = (log: AuditLog) => !this.usersHash[log.userId];
                break;
            case MessagesComponent.GROUP_BY_MESSAGE_TYPE:
                groupField = (log: AuditLog) => JSON.stringify(log.metadata.types);
                groupLabel = (log: AuditLog) => log.metadata.types.join(' + ');
                isRemoved  = (log: AuditLog) => false;
                break;
            case MessagesComponent.GROUP_BY_LOCALE:
                this.languages.forEach((value: GroupedLanguages) => {
                    value.languages.forEach((value: Language) => {
                        languageIdHash[value.id] = value;
                    });
                });

                groupField = (log: AuditLog) => log.metadata.languageId;
                groupLabel = (log: AuditLog) => languageIdHash[log.metadata.languageId].label;
                isRemoved  = (log: AuditLog) => false;
                break;
            case MessagesComponent.GROUP_BY_DATE:
                groupField = (log: AuditLog) => moment(log.created).format('YYYYMMDD');
                groupLabel = (log: AuditLog) => moment(log.created).format('DD-MM-YYYY');
                isRemoved  = (log: AuditLog) => false;
                break;
        }

        audits.forEach((log: AuditLog) => {
            // Determine all the filters available for the current set of audit logs
            if (availableFilters.companyIds.indexOf(log.companyId) === -1) {
                availableFilters.companyIds.push(log.companyId);
            }

            if (availableFilters.userIds.indexOf(log.userId) === -1) {
                availableFilters.userIds.push(log.userId);
            }

            if (availableFilters.languages.indexOf(log.metadata.languageId) === -1) {
                availableFilters.languages.push(log.metadata.languageId);
            }

            if (availableFilters.messageTypes.indexOf(log.metadata.types.join(' + ')) === -1) {
                availableFilters.messageTypes.push(log.metadata.types.join(' + '));
            }

            if (availableFilters.behaviours.indexOf(log.metadata.behaviour) === -1) {
                availableFilters.behaviours.push(log.metadata.behaviour);
            }

            log.metadata.attachmentTypes.forEach((type: string) => {
                if (availableFilters.attachments.indexOf(type) === -1) {
                    availableFilters.attachments.push(type);
                }
            });

            // Filter out the audits which doesn't match the filter selection
            if (this.filters.company && this.filters.company.id !== null && log.companyId !== this.filters.company.id) {
                return;
            }

            if (this.filters.user && this.filters.user.id !== null && log.userId !== this.filters.user.id) {
                return;
            }

            if (this.filters.languageId !== null && this.filters.languageId !== log.metadata.languageId) {
                return;
            }

            if (this.filters.behaviour !== null && this.filters.behaviour !== log.metadata.behaviour) {
                return;
            }

            if (this.filters.attachment !== null && (!log.metadata.attachmentTypes || log.metadata.attachmentTypes.indexOf(this.filters.attachment) === -1)) {
                return;
            }

            if (this.filters.type && this.filters.type.types.length) {
                if (!log.metadata.types || log.metadata.types.length !== this.filters.type.types.length) {
                    return;
                }

                for (let i = 0; i < this.filters.type.types.length; i++) {
                    if (this.filters.type.types[i] !== log.metadata.types[i]) {
                        return;
                    }
                }
            }

            let hashKey = groupField(log);

            if (this.rowHash[hashKey] === undefined) {
                let row           = new MessageStatisticRow();
                row.name          = groupLabel(log);
                row.removedRecord = isRemoved(log);

                this.rows.push(row);
                this.rowHash[hashKey] = row;
            }

            switch (log.event) {
                case AuditLog.EVENT_MESSAGE_CREATED:
                    this.rowHash[hashKey].written++;
                    this.rowHash[hashKey].total++;
                    this.rowTotal.written++;
                    this.rowTotal.total++;
                    break;
                case AuditLog.EVENT_MESSAGE_APPROVED:
                    this.rowHash[hashKey].approved++;
                    this.rowHash[hashKey].total++;
                    this.rowTotal.approved++;
                    this.rowTotal.total++;
                    break;
                case AuditLog.EVENT_MESSAGE_DISAPPROVED:
                    this.rowHash[hashKey].disapproved++;
                    this.rowHash[hashKey].total++;
                    this.rowTotal.disapproved++;
                    this.rowTotal.total++;
                    break;
                case AuditLog.EVENT_MESSAGE_DELETED:
                    this.rowHash[hashKey].deleted++;
                    this.rowHash[hashKey].total++;
                    this.rowTotal.deleted++;
                    this.rowTotal.total++;
                    break;
            }
        });

        this.sortRows();

        if (reloadFilters === true) {
            this.createFilterOptions(availableFilters);
        }
    }

    filtersMutated(reloadFromApi: boolean = true, reloadFilters: boolean = true) {
        this.filters.isMutated = true;
        this.reload(reloadFromApi, reloadFilters);
    }

    reload(reloadFromApi: boolean = true, reloadFilters: boolean = true) {
        if (reloadFromApi === false) {
            this.populateStatisticTable(this.audits, reloadFilters);
            return;
        }

        this.isLoading   = true;
        this.audits      = [];
        this.rows    = [];
        this.rowHash = {};

        Observable.create((observer: Observer<any>) => {
            this.loadData(1, observer, []);
        }).subscribe((audits: AuditLog[]) => {
            this.audits    = audits;
            this.isLoading = false;

            if (this.filters.isMutated === false) {
                this.resetFilters();
            }

            this.populateStatisticTable(this.audits, reloadFilters);
        });
    }

    private loadData(page, mainObserver, audits: AuditLog[] = []) {
        if (page === null) {
            mainObserver.next(audits);
            mainObserver.complete();

            return;
        }

        let dateRange    = this.getDateRange();

        this.api.audits().collection(
            page,
            5000,
            dateRange[0].set({'hour' : 0, 'minute' : 0, 'second' : 0}).utc().format(),
            dateRange[1].set({'hour' : 23, 'minute' : 59, 'second' : 59}).utc().format(),
            [
                AuditLog.EVENT_MESSAGE_CREATED,
                AuditLog.EVENT_MESSAGE_APPROVED,
                AuditLog.EVENT_MESSAGE_DISAPPROVED,
                AuditLog.EVENT_MESSAGE_DELETED
            ]
        ).subscribe((response: AuditLogResponse) => {
            response.result.forEach((audit: AuditLog) => audits.push(audit));
            this.loadData(response.pagination.next, mainObserver, audits);
        });
    }

    startChanges(date: NgbDateStruct) {
        this.endMinDate = date;
        this.filters.dateRangeFrom = date;
        this.reload();
    }

    endChanges(date: NgbDateStruct) {
        this.startMaxDate = date;
        this.filters.dateRangeTill = date;
        this.reload();
    }

    rangeOptionChanges() {
        this.resetCustomDates();
        this.reload();
    }

    protected getDateRange(): any[] {
        let from = moment();
        let to   = moment();

        switch (this.filters.dateRangeOption) {
            case MessagesComponent.DATE_RANGE_TODAY:
                break;
            case MessagesComponent.DATE_RANGE_YESTERDAY:
                from.subtract(1, 'days');
                to.subtract(1, 'days');
                break;
            case MessagesComponent.DATE_RANGE_THIS_WEEK:
                from.startOf('isoWeek');
                break;
            case MessagesComponent.DATE_RANGE_THIS_MONTH:
                from.startOf('month');
                break;
            case MessagesComponent.DATE_RANGE_PREVIOUS_WEEK:
                from.startOf('isoWeek').subtract(1, 'days').startOf('isoWeek');
                to.startOf('isoWeek').subtract(1, 'days').endOf('isoWeek');
                break;
            case MessagesComponent.DATE_RANGE_PREVIOUS_MONTH:
                from.startOf('month').subtract(1, 'days').startOf('month');
                to.startOf('month').subtract(1, 'days').endOf('month');
                break;
            case MessagesComponent.DATE_RANGE_THIS_YEAR:
                from.startOf('year').startOf('month');
                break;
            case MessagesComponent.DATE_RANGE_CUSTOM:
                from.set({
                    'year' : this.filters.dateRangeFrom.year,
                    'month' : this.filters.dateRangeFrom.month - 1,
                    'date' : this.filters.dateRangeFrom.day
                });

                to.set({
                    'year' : this.filters.dateRangeTill.year,
                    'month' : this.filters.dateRangeTill.month - 1,
                    'date' : this.filters.dateRangeTill.day
                });

                break;
        }

        from.set({'hour' : 0, 'minute' : 0, 'second' : 0});
        to.set({'hour' : 23, 'minute' : 59, 'second' : 59});

        return [from, to];
    }

    protected toggleSort(column: string) {
        if (column !== this.sortColumn) {
            this.sortColumn    = column;
            this.sortDirection = MessagesComponent.SORT_ASC;

            return this.sortRows();
        }

        if (this.sortDirection === null) {
            this.sortDirection = MessagesComponent.SORT_ASC;
        } else {
            this.sortDirection = (this.sortDirection === MessagesComponent.SORT_DESC) ?
                MessagesComponent.SORT_ASC :
                MessagesComponent.SORT_DESC;
        }

        return this.sortRows();
    }

    protected sortRows() {
        if (this.sortColumn === null) {
            return;
        }

        this.rows.sort((a: MessageStatisticRow, b: MessageStatisticRow) => {
            if (a[this.sortColumn] < b[this.sortColumn]) {
                return this.sortDirection === MessagesComponent.SORT_ASC ? -1 : 1;
            } else if (a[this.sortColumn] > b[this.sortColumn]) {
                return this.sortDirection === MessagesComponent.SORT_ASC ? 1 : -1;
            }

            return 0;
        });
    }

    private createFilterOptions(availableFilters: AvailableFilters) {
        this.resetCustomDates();

        // Contruct the company filter
        this.companies = [];
        this.route.snapshot.data['companies'].forEach((company: Company) => {
            if (availableFilters.companyIds.indexOf(company.id) !== -1) {
                this.companies.push(company);
            }
        });

        // Add current company if it isn't in the list
        if (this.filters.company && this.companies.indexOf(this.filters.company) === -1) {
            this.companies.push(this.filters.company);
        }

        this.utils.sortList(this.companies, 'name');

        // When 1 company is selected. Choose that one
        if (this.companies.length === 1 && this.companies[0] !== this.filters.company) {
            this.filters.company = this.companies[0];
        }

        // Construct the user filter
        this.users     = [];
        this.route.snapshot.data['users'].forEach((user: User) => {
            if (availableFilters.userIds.indexOf(user.id) !== -1) {
                this.users.push(user);
            }
        });

        // Add current filter if it isn't in the list
        if (this.filters.user && this.users.indexOf(this.filters.user) === -1) {
            this.users.push(this.filters.user);
        }

        this.utils.sortList(this.users, 'username');

        if (this.users.length === 1 && this.users[0] !== this.filters.user) {
            this.filters.user = this.users[0];
        }

        // Construct languages filter
        this.languages          = [];
        this.languagesAvailable = 0;

        this.route.snapshot.data['languages'].forEach((group: GroupedLanguages) => {
            group                     = JSON.parse(JSON.stringify(group)); // Create a deep copy
            let languages: Language[] = [];

            group.languages.forEach((language: Language) => {
                if (availableFilters.languages.indexOf(language.id) !== -1) {
                    languages.push(language);
                    this.languagesAvailable++;
                }
            });

            if (languages.length > 0) {
                group.languages = languages;
                this.languages.push(group);
            }
        });

        this.types = [];
        let selectedFound: boolean = this.filters.type === null;
        availableFilters.messageTypes.forEach((filter: string) => {
            this.types.push({
                label : filter,
                types : filter.split(' + ')
            });

            if (this.filters.type && this.filters.type.label === filter) {
                selectedFound = true;
                this.filters.type = this.types[this.types.length - 1];
            }
        });

        // Make sure the selected one is always in the list
        if (selectedFound === false) {
            this.types.push(this.filters.type);
        }

        this.utils.sortList(this.types, 'label');

        this.behaviours = [];
        MessageCollection.behaviours.forEach((behaviour: string) => {
            if (availableFilters.behaviours.indexOf(behaviour) !== -1) {
                this.behaviours.push(behaviour);
            }
        });

        // Make sure the selected one is always in the list
        if (this.filters.behaviour && this.behaviours.indexOf(this.filters.behaviour) === -1) {
            this.behaviours.push(this.filters.behaviour);
        }

        this.behaviours.sort();
        if (this.behaviours.length === 1 && this.behaviours[0] !== this.filters.behaviour) {
            this.filters.behaviour = this.behaviours[0];
        }

        this.attachments = [];
        Attachment.types.forEach((attachment: string) => {
            if (availableFilters.attachments.indexOf(attachment) !== -1) {
                this.attachments.push(attachment);
            }
        });

        // Make sure the selected one is always in the list
        if (this.filters.attachment && this.attachments.indexOf(this.filters.attachment) === -1) {
            this.attachments.push(this.filters.attachment);
        }

        this.attachments.sort();
    }

    protected resetFilters(reload: boolean = false) {
        let dateRangeOptions: any = (this.filters) ? {
            dateRangeOption: this.filters.dateRangeOption,
            dateRangeFrom: this.filters.dateRangeFrom,
            dateRangeTill: this.filters.dateRangeTill,
        } : {
            dateRangeOption: MessagesComponent.DATE_RANGE_TODAY,
            dateRangeFrom: null,
            dateRangeTill: null,
        };

        this.filters = {
            dateRangeOption: dateRangeOptions.dateRangeOption,
            dateRangeFrom: dateRangeOptions.dateRangeFrom,
            dateRangeTill: dateRangeOptions.dateRangeTill,
            company: null,
            user: null,
            languageId: null,
            behaviour: null,
            attachment: null,
            type: null,
            groupBy: MessagesComponent.GROUP_BY_USER,
            isMutated: false
        };

        this.showCompanyFilter = this.auth.canDoAny([Permission.COMPANIES_READ]);
        this.showUsersFilter   = this.auth.canDoAny([Permission.USERS_READ, Permission.USERS_READ_ALL]);

        if (this.showUsersFilter === false) {
            this.groupBys.splice(this.groupBys.indexOf(MessagesComponent.GROUP_BY_USER), 1);
            this.filters.groupBy = MessagesComponent.GROUP_BY_MESSAGE_TYPE;
        }

        this.resetCustomDates();

        if (reload === true) {
            this.reload(false, true);
        }
    }

    private resetCustomDates() {
        let dateRange = this.getDateRange();

        this.startMinDate = {
            day   : 1,
            month : 3,
            year  : 2018
        };

        this.startMaxDate = {
            day   : dateRange[1].date(),
            month : dateRange[1].month() + 1,
            year  : dateRange[1].year()
        };

        this.endMinDate = {
            day   : dateRange[0].date(),
            month : dateRange[0].month() + 1,
            year  : dateRange[0].year()
        };

        this.endMaxDate = {
            day   : moment().date(),
            month : moment().month() + 1,
            year  : moment().year()
        };

        this.filters.dateRangeFrom = {
            day   : dateRange[0].date(),
            month : dateRange[0].month() + 1,
            year  : dateRange[0].year()
        };

        this.filters.dateRangeTill = {
            day   : dateRange[1].date(),
            month : dateRange[1].month() + 1,
            year  : dateRange[1].year()
        };
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/statistics/messages/messages.component.ts